import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, API_URL } from '../../services/apiService';
import { Article, CATEGORY_LABELS, ValidationStageResult } from '../../types';
import { resolveImageUrl } from '../../utils/images';
import jsPDF from 'jspdf';

// ─── Content formatter: renders plain article prose for the editor ───────────
function formatArticleContent(text: string): string {
  return text
    .split('\n')
    .map(line => {
      const trimmed = line.trim();
      // Drop any bold section-header lines that slipped through backend processing
      if (/^\*\*[^*]+\*\*:?\s*$/.test(trimmed)) return '';
      // Strip any remaining **bold** markers — article body is plain prose
      return line.replace(/\*\*([^*]+)\*\*/g, '$1');
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')        // max 2 consecutive blank lines
    .replace(/\n\n/g, '<br/><br/>')    // paragraph breaks
    .replace(/\n/g, '<br/>');
}

// ─── Score helpers ────────────────────────────────────────────────────────────
const scoreColor = (v: number) =>
  v >= 85 ? '#34d399' : v >= 70 ? '#4f46e5' : v >= 50 ? '#f59e0b' : '#ef4444';

const scoreLabel = (v: number) =>
  v >= 90 ? 'PUBLICATION READY' : v >= 70 ? 'NEEDS REVIEW' : 'NEEDS REWRITE';

// ─── Component ────────────────────────────────────────────────────────────────
const ArticleInspector: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();

  const [article, setArticle]       = useState<Article | null>(null);
  const [activeTab, setActiveTab]   = useState<'quality' | 'image' | 'history'>('quality');
  const [feedback, setFeedback]     = useState('');
  const [isRevising, setIsRevising] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [reviseSuccess, setReviseSuccess] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, kind: 'success' | 'error') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  };

  // Editor state
  const editorRef = useRef<HTMLDivElement>(null);
  const [isDirty, setIsDirty]   = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt]   = useState<Date | null>(null);
  const [wordCount, setWordCount] = useState(0);
  const dirtyRef = useRef(false); // tracks dirty without stale closure

  // Load article
  useEffect(() => {
    if (!articleId) return;
    apiService.getArticle(articleId).then(data => {
      const found = data?.article;
      if (found) {
        setArticle(found);
        setWordCount(found.content.split(/\s+/).filter(Boolean).length);
      }
    }).catch(() => {});
  }, [articleId]);

  // Seed editor content once article loads
  useEffect(() => {
    if (article && editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = formatArticleContent(article.content);
    }
  }, [article]);

  const handleEditorInput = useCallback(() => {
    if (!editorRef.current) return;
    const text = editorRef.current.innerText || '';
    setWordCount(text.split(/\s+/).filter(Boolean).length);
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setIsDirty(true);
    }
  }, []);

  const handleSave = async () => {
    if (!article || !editorRef.current) return;
    setIsSaving(true);
    try {
      const text = editorRef.current.innerText || '';
      await apiService.updateArticleContent(article.id, text);
      setArticle(prev => prev ? { ...prev, content: text } : prev);
      dirtyRef.current = false;
      setIsDirty(false);
      setSavedAt(new Date());
    } catch (err) {
      showToast('Save failed — ' + (err as Error).message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevise = async () => {
    if (!article || !feedback.trim()) return;
    setIsRevising(true);
    setReviseSuccess(false);
    try {
      const response = await apiService.reviseArticle(
        article.id, feedback, article.workflow_id || 'workflow_0'
      );
      if (response.status === 'revised') {
        const updated = { ...article, ...response, content: response.content };
        setArticle(updated);
        // Re-seed editor
        if (editorRef.current) {
          editorRef.current.innerHTML = formatArticleContent(response.content);
        }
        setWordCount(response.content.split(/\s+/).filter(Boolean).length);
        dirtyRef.current = false;
        setIsDirty(false);
        setFeedback('');
        setReviseSuccess(true);
        setTimeout(() => setReviseSuccess(false), 4000);
      }
    } catch (err) {
      showToast('Revision failed — ' + (err as Error).message, 'error');
    } finally {
      setIsRevising(false);
    }
  };

  const handlePublish = async () => {
    if (!article) return;
    setIsPublishing(true);
    try {
      await apiService.publishArticle(article.id);
      setArticle({ ...article, status: 'published' });
    } catch (err) {
      showToast('Publish failed — ' + (err as Error).message, 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleExportPDF = async () => {
    if (!article) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 50;
    const contentW = pageW - margin * 2;
    let y = margin;
    const checkY = (n: number) => { if (y + n > pageH - margin) { doc.addPage(); y = margin; } };

    doc.setFillColor(232, 136, 42);
    doc.rect(0, 0, pageW, 5, 'F');
    y = 28;

    const dateStr = (article.created_at ? new Date(article.created_at) : new Date())
      .toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(115, 115, 115);
    doc.text(dateStr, pageW - margin, y, { align: 'right' }); y += 18;

    if (article.category) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(232, 136, 42);
      doc.text((CATEGORY_LABELS[article.category] || article.category).toUpperCase(), margin, y);
      y += 14;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(15, 19, 28);
    const hLines = doc.splitTextToSize(article.headline, contentW);
    doc.text(hLines, margin, y); y += hLines.length * 22 + 8;

    const sources = article.all_sources?.length
      ? article.all_sources
      : article.news_source ? [{ name: article.news_source, url: article.news_url || '' }] : [];
    if (sources.length) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(115, 115, 115);
      doc.text(`SOURCES (${sources.length})`, margin, y); y += 12;
      sources.forEach((s, i) => {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(115, 115, 115);
        const num = `${i + 1}. `;
        doc.text(num, margin, y);
        doc.setTextColor(30, 64, 175);
        s.url ? doc.textWithLink(s.name || s.url, margin + doc.getTextWidth(num), y, { url: s.url })
               : doc.text(s.name, margin + doc.getTextWidth(num), y);
        y += 12;
      });
      y += 4;
    }

    doc.setDrawColor(220, 220, 220); doc.line(margin, y, pageW - margin, y); y += 16;

    const rawImg = article.generated_image_url || article.imageUrl;
    if (rawImg) {
      const imgUrl = rawImg.startsWith('/')
        ? `${API_URL}/api/image-proxy?path=${encodeURIComponent(rawImg)}` : rawImg;
      try {
        const dataUrl: string = await new Promise(async (res, rej) => {
          const r = await fetch(imgUrl);
          if (!r.ok) throw new Error(`${r.status}`);
          const blob = await r.blob();
          const fr = new FileReader();
          fr.onload = () => res(fr.result as string);
          fr.onerror = () => rej(new Error('read failed'));
          fr.readAsDataURL(blob);
        });
        const el = await new Promise<HTMLImageElement>(res => {
          const img = new Image(); img.onload = () => res(img); img.onerror = () => res(img); img.src = dataUrl;
        });
        const mw = contentW, mh = 240;
        const ar = el.naturalWidth > 0 ? el.naturalHeight / el.naturalWidth : 9 / 16;
        const ih = Math.min(mh, mw * ar);
        checkY(ih + 16);
        doc.addImage(dataUrl, dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG', margin, y, mw, ih);
        y += ih + 16;
      } catch {}
    }

    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(80, 80, 80);
    for (const para of article.content.split(/\n\n+/)) {
      const t = para.trim(); if (!t) continue;
      const lines = doc.splitTextToSize(t, contentW);
      checkY(lines.length * 14 + 8); doc.text(lines, margin, y); y += lines.length * 14 + 8;
    }

    const total = (doc.internal as any).getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setDrawColor(220, 220, 220); doc.line(margin, pageH - 36, pageW - margin, pageH - 36);
      doc.setFontSize(8); doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 22, { align: 'right' });
    }
    doc.save(`${article.headline.replace(/[^a-z0-9]/gi, '_').slice(0, 50)}.pdf`);
  };

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (!article) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-3">
          <span className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#4f46e5', borderTopColor: 'transparent' }} />
          <span className="font-mono text-label-sm" style={{ color: '#94a3b8' }}>Loading article...</span>
        </div>
      </div>
    );
  }

  const imageUrl = resolveImageUrl(article.generated_image_url || article.imageUrl);
  const qualityScore   = article.style_compliance_score || article.confidence_score || 0;
  const originalityScore = article.originality_score || (100 - (article.plagiarism_score || 0));
  const overallScore   = Math.round(((article.style_match_score || 0) + originalityScore + qualityScore) / 3);
  const isPublished    = article.status === 'published';

  const saveStatus = isSaving
    ? 'Saving…'
    : isDirty
    ? '● Unsaved changes'
    : savedAt
    ? `Saved ${savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
    : 'No changes';

  const saveStatusColor = isDirty ? '#f59e0b' : savedAt ? '#34d399' : '#94a3b8';

  return (
    <div className="flex flex-col w-full gap-0">

      {/* ── Toast ─────────────────────────────────────────────────── */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg font-body text-body-sm"
          style={{
            background: toast.kind === 'success' ? 'rgba(52,211,153,0.12)' : '#fef2f2',
            border: `1px solid ${toast.kind === 'success' ? 'rgba(52,211,153,0.4)' : '#fecaca'}`,
            color: toast.kind === 'success' ? '#065f46' : '#dc2626',
          }}
        >
          <span className="material-symbols-outlined text-[18px]">
            {toast.kind === 'success' ? 'check_circle' : 'error'}
          </span>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-2 opacity-50 hover:opacity-100">
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>
      )}

      {/* ── Top bar: breadcrumb + actions ─────────────────────────── */}
      <div
        className="sticky top-16 z-30 w-full flex items-center justify-between gap-4 px-4 py-2.5 mb-5"
        style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        {/* Left: breadcrumb */}
        <button
          onClick={() => navigate('/content')}
          className="inline-flex items-center gap-1.5 font-body text-body-sm transition-colors flex-shrink-0"
          style={{ color: '#64748b' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#4f46e5'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#64748b'; }}
        >
          <span className="material-symbols-outlined text-[16px]">arrow_back</span>
          Articles
        </button>

        {/* Centre: save status */}
        <span className="font-mono text-label-xs" style={{ color: saveStatusColor }}>{saveStatus}</span>

        {/* Right: actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isDirty && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body text-body-sm transition-all disabled:opacity-50"
              style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', color: '#d97706' }}
            >
              <span className="material-symbols-outlined text-[14px]">save</span>
              Save
            </button>
          )}

          <button
            onClick={() => navigate(`/feedback?articleId=${article.id}`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body text-body-sm transition-all"
            style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569' }}
          >
            <span className="material-symbols-outlined text-[14px]">rate_review</span>
            Feedback
          </button>

          <button
            onClick={handleExportPDF}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body text-body-sm transition-all"
            style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569' }}
          >
            <span className="material-symbols-outlined text-[14px]">picture_as_pdf</span>
            PDF
          </button>

          <button
            onClick={handlePublish}
            disabled={isPublishing || isPublished}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-display text-headline-sm transition-all disabled:opacity-60"
            style={{
              background: isPublished ? 'rgba(52,211,153,0.12)' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              color: isPublished ? '#34d399' : '#ffffff',
              border: isPublished ? '1px solid rgba(52,211,153,0.3)' : 'none',
              boxShadow: isPublished ? 'none' : '0 3px 12px rgba(79,70,229,0.3)',
            }}
          >
            <span className="material-symbols-outlined text-[16px]">
              {isPublished ? 'done_all' : isPublishing ? 'progress_activity' : 'rocket_launch'}
            </span>
            {isPublished ? 'Published' : isPublishing ? 'Publishing…' : 'Mark as Published'}
          </button>
        </div>
      </div>

      {/* ── Article header ────────────────────────────────────────── */}
      <div className="px-1 mb-5">
        {/* Category + status row */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {article.category && (
            <span className="font-mono text-label-xs px-2 py-0.5 rounded uppercase font-bold"
              style={{ background: 'rgba(79,70,229,0.1)', color: '#4f46e5' }}>
              {CATEGORY_LABELS[article.category] || article.category}
            </span>
          )}
          {article.sub_vertical && (
            <span className="font-mono text-label-xs px-2 py-0.5 rounded"
              style={{ background: '#f1f5f9', color: '#64748b' }}>
              {article.sub_vertical}
            </span>
          )}
          <StatusChip status={article.status} />
          {(article.revision_count || 0) > 0 && (
            <span className="font-mono text-label-xs" style={{ color: '#94a3b8' }}>
              Revised ×{article.revision_count}
            </span>
          )}
        </div>

        {/* Headline */}
        <h1 className="font-display text-headline-lg tracking-tight mb-2" style={{ color: '#0f172a', letterSpacing: '-0.02em' }}>
          {article.headline}
        </h1>

        {/* Subheading */}
        {article.subheading && (
          <p className="font-body text-body-lg max-w-3xl mb-3" style={{ color: '#475569', lineHeight: 1.6 }}>
            {article.subheading}
          </p>
        )}

        {/* Meta strip */}
        <div className="flex flex-wrap items-center gap-4">
          <MetaItem icon="calendar_today" text={article.created_at
            ? new Date(article.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
            : 'N/A'} />
          <MetaItem icon="segment" text={`${wordCount} words`} />
          <MetaItem icon="link" href={article.news_url} text={article.news_source} />
          <span className="flex items-center gap-1.5 font-mono text-label-sm font-bold" style={{ color: scoreColor(overallScore) }}>
            <span className="material-symbols-outlined text-[15px]">verified_user</span>
            {overallScore}% Quality
          </span>
          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]" style={{ color: '#94a3b8' }}>label</span>
              {article.tags.slice(0, 4).map(tag => (
                <span key={tag} className="px-2 py-0.5 rounded font-body text-[10px]"
                  style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Hero image (full width if exists) ────────────────────── */}
      {imageUrl && (
        <div className="w-full rounded-xl overflow-hidden mb-5" style={{ height: 340, background: '#0f172a' }}>
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover"
            style={{ display: 'block' }}
          />
        </div>
      )}

      {/* ── Two-column body ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 w-full">

        {/* ── LEFT: Article editor (7 cols) ─────────────────────── */}
        <div className="lg:col-span-7 flex flex-col gap-4">

          {/* Editor card */}
          <div className="rounded-xl overflow-hidden flex flex-col"
            style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>


            {/* Editable content */}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleEditorInput}
              className="p-6 focus:outline-none"
              style={{
                fontFamily: '"Inter", system-ui, sans-serif',
                fontSize: 16,
                lineHeight: 1.85,
                color: '#1e293b',
                minHeight: 480,
              }}
            />
          </div>

          {/* Source grounding */}
          {(article.all_sources?.length || article.news_source) && (
            <div className="rounded-xl p-4"
              style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-[16px]" style={{ color: '#4f46e5' }}>travel_explore</span>
                <span className="font-display text-headline-sm" style={{ color: '#0f172a' }}>Source Grounding</span>
                <span className="font-mono text-label-xs ml-1" style={{ color: '#94a3b8' }}>
                  {article.all_sources?.length || 1} source{(article.all_sources?.length || 1) > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {(article.all_sources?.length
                  ? article.all_sources
                  : [{ name: article.news_source, url: article.news_url || '' }]
                ).map((src, idx) => {
                  const isGoogle = src.url.includes('news.google.com/rss');
                  return (
                    <div key={idx} className="flex items-start justify-between gap-3 p-3 rounded-lg"
                      style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0"
                          style={{ background: 'rgba(79,70,229,0.1)', color: '#4f46e5' }}>
                          REF {idx + 1}
                        </span>
                        <span className="font-body text-body-sm font-semibold truncate" style={{ color: '#0f172a' }}>{src.name}</span>
                      </div>
                      {!isGoogle && src.url && (
                        <a href={src.url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 font-body text-[11px] flex-shrink-0 transition-colors"
                          style={{ color: '#94a3b8' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#4f46e5'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#94a3b8'; }}
                        >
                          <span className="material-symbols-outlined text-[12px]">open_in_new</span>
                          Visit source
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: Quality + Revision (5 cols) ────────────────── */}
        <div className="lg:col-span-5 flex flex-col gap-4">

          {/* Quality panel */}
          <div className="rounded-xl overflow-hidden"
            style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

            {/* Tabs */}
            <div className="flex items-center gap-1 p-1.5" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {[
                { key: 'quality', icon: 'verified', label: 'Quality' },
                { key: 'image',   icon: 'image',    label: 'Cover Image' },
                { key: 'history', icon: 'history',  label: 'History', badge: (article.revision_history?.length || 0) + 1 },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key as any)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body text-body-sm flex-1 justify-center transition-all"
                  style={{
                    background: activeTab === t.key ? '#ffffff' : 'transparent',
                    color: activeTab === t.key ? '#4f46e5' : '#64748b',
                    boxShadow: activeTab === t.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    fontWeight: activeTab === t.key ? 600 : 400,
                  }}
                >
                  <span className="material-symbols-outlined text-[15px]">{t.icon}</span>
                  {t.label}
                  {(t as any).badge > 0 && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, minWidth: 16, height: 16,
                      borderRadius: 8, background: activeTab === t.key ? '#4f46e5' : '#e2e8f0',
                      color: activeTab === t.key ? '#fff' : '#64748b',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                    }}>
                      {(t as any).badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Quality tab */}
            {activeTab === 'quality' && (
              <div className="p-4 flex flex-col gap-4">

                {/* Overall score */}
                <div className="rounded-xl p-4 flex items-center justify-between"
                  style={{ background: `${scoreColor(overallScore)}0f`, border: `1px solid ${scoreColor(overallScore)}30` }}>
                  <div>
                    <div className="font-mono text-label-xs uppercase tracking-wider mb-1" style={{ color: '#94a3b8' }}>Overall Score</div>
                    <div className="font-display" style={{ fontSize: 42, lineHeight: 1, color: scoreColor(overallScore), letterSpacing: '-0.03em' }}>
                      {overallScore}
                      <span className="font-body text-body-md ml-1" style={{ color: '#94a3b8' }}>/100</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 font-mono text-label-xs font-bold" style={{ color: scoreColor(overallScore) }}>
                      <span className="material-symbols-outlined text-[13px]">
                        {overallScore >= 90 ? 'check_circle' : overallScore >= 70 ? 'pending' : 'cancel'}
                      </span>
                      {scoreLabel(overallScore)}
                    </div>
                  </div>
                  <ScoreRing value={overallScore} />
                </div>

                {/* Score breakdown */}
                <div className="flex flex-col gap-2">
                  <ScoreRow label="Style Compliance"   value={article.style_match_score || 0} />
                  <ScoreRow label="Originality"     value={originalityScore} />
                  <ScoreRow label="Fact Confidence" value={qualityScore} />
                </div>

                {/* Validation gates — show only when at least one stage result is present */}
                {(article.final_qa_stage_result || article.fact_integrity_stage_result) && (
                  <div className="flex flex-col gap-1.5">
                    <div className="font-mono text-label-xs uppercase tracking-wider" style={{ color: '#94a3b8' }}>
                      Validation Gates
                    </div>
                    <ValidationStageBadge label="Final QA" result={article.final_qa_stage_result} />
                    <ValidationStageBadge label="Fact Integrity" result={article.fact_integrity_stage_result} />
                  </div>
                )}
              </div>
            )}

            {/* History tab */}
            {activeTab === 'history' && (
              <div className="p-4 flex flex-col gap-0" style={{ maxHeight: 540, overflowY: 'auto' }}>
                <ArticleHistory article={article} />
              </div>
            )}

            {/* Cover image tab */}
            {activeTab === 'image' && (
              <div className="p-4 flex flex-col gap-4">
                {imageUrl ? (
                  <>
                    <div className="rounded-xl overflow-hidden" style={{ background: '#0f172a' }}>
                      <img src={imageUrl} alt="" className="w-full object-cover" style={{ display: 'block', maxHeight: 260 }} />
                    </div>
                    {article.image_prompt && (
                      <div>
                        <div className="font-mono text-label-xs uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                          Prompt Used
                        </div>
                        <div className="p-3 rounded-lg font-body text-body-sm" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', lineHeight: 1.6 }}>
                          {article.image_prompt}
                        </div>
                      </div>
                    )}
                    {article.image_confidence_score != null && (
                      <div className="flex items-center justify-between px-3 py-2 rounded-lg"
                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <span className="font-mono text-label-xs" style={{ color: '#94a3b8' }}>Image Confidence</span>
                        <span className="font-mono text-label-sm font-bold" style={{ color: scoreColor(article.image_confidence_score * 100) }}>
                          {Math.round(article.image_confidence_score * 100)}%
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 rounded-xl"
                    style={{ background: '#f8fafc', border: '2px dashed #e2e8f0' }}>
                    <span className="material-symbols-outlined text-[36px] mb-2" style={{ color: '#cbd5e1' }}>image_not_supported</span>
                    <p className="font-body text-body-sm" style={{ color: '#94a3b8' }}>No image generated yet</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Revision panel */}
          <div className="rounded-xl overflow-hidden"
            style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}>
            <div className="px-4 py-3 flex items-center justify-between"
              style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[16px]" style={{ color: '#4f46e5' }}>cached</span>
                <span className="font-display text-headline-sm" style={{ color: '#0f172a' }}>Quick Revision</span>
              </div>
              <button
                onClick={() => navigate(`/feedback?articleId=${article.id}`)}
                className="flex items-center gap-1 font-body text-body-sm transition-colors"
                style={{ color: '#94a3b8' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#4f46e5'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#94a3b8'; }}
              >
                Full feedback desk
                <span className="material-symbols-outlined text-[13px]">arrow_forward</span>
              </button>
            </div>
            <div className="p-4 flex flex-col gap-3">
              {reviseSuccess && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg"
                  style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)' }}>
                  <span className="material-symbols-outlined text-[15px]" style={{ color: '#34d399' }}>check_circle</span>
                  <span className="font-body text-body-sm" style={{ color: '#065f46' }}>Article revised successfully</span>
                </div>
              )}
              <textarea
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="Describe what to change — e.g. 'The LME price is wrong, correct it to $2,280/t' or 'Lead paragraph is too promotional'…"
                rows={4}
                className="w-full rounded-xl p-3 font-body text-body-sm resize-none focus:outline-none transition-all"
                style={{
                  background: '#f8fafc',
                  border: '1.5px solid #e2e8f0',
                  color: '#0f172a',
                  lineHeight: 1.6,
                }}
                onFocus={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#fff'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#f8fafc'; }}
              />
              <button
                onClick={handleRevise}
                disabled={isRevising || !feedback.trim()}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-display text-headline-sm transition-all disabled:opacity-50"
                style={{
                  background: isRevising ? '#f1f5f9' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                  color: isRevising ? '#94a3b8' : '#ffffff',
                  boxShadow: isRevising ? 'none' : '0 3px 12px rgba(79,70,229,0.3)',
                }}
              >
                <span className={`material-symbols-outlined text-[16px] ${isRevising ? 'animate-spin' : ''}`}>
                  {isRevising ? 'progress_activity' : 'cached'}
                </span>
                {isRevising ? 'Revising…' : 'Revise with AI'}
              </button>
              {(article.revision_count || 0) > 0 && (
                <p className="text-center font-mono text-label-xs" style={{ color: '#94a3b8' }}>
                  Revised {article.revision_count}× already
                </p>
              )}
            </div>
          </div>

          {/* Generated date */}
          <div className="flex items-center justify-between px-4 py-3 rounded-xl"
            style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}>
            <span className="font-mono text-label-xs flex items-center gap-1.5" style={{ color: '#94a3b8' }}>
              <span className="material-symbols-outlined text-[14px]">schedule</span>
              Generated
            </span>
            <span className="font-mono text-label-sm font-semibold" style={{ color: '#475569' }}>
              {article.created_at
                ? new Date(article.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const MetaItem: React.FC<{ icon: string; text: string; href?: string }> = ({ icon, text, href }) => (
  <div className="flex items-center gap-1.5 font-mono text-label-sm" style={{ color: '#64748b' }}>
    <span className="material-symbols-outlined text-[14px]" style={{ color: '#94a3b8' }}>{icon}</span>
    {href
      ? <a href={href} target="_blank" rel="noreferrer" style={{ color: '#64748b' }} className="hover:underline">{text}</a>
      : <span>{text}</span>
    }
  </div>
);

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const cfg: Record<string, { color: string; bg: string; label: string }> = {
    draft:        { color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)',  label: 'Draft' },
    published:    { color: '#34d399', bg: 'rgba(52,211,153,0.1)',  label: 'Published' },
    revised:      { color: '#4f46e5', bg: 'rgba(79,70,229,0.1)',  label: 'Revised' },
    needs_review: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   label: 'Needs Review' },
    review:       { color: '#64748b', bg: 'rgba(100,116,139,0.1)', label: 'In Review' },
  };
  const c = cfg[status] || { color: '#94a3b8', bg: '#f1f5f9', label: status };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-[10px] font-semibold"
      style={{ color: c.color, background: c.bg }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} />
      {c.label}
    </span>
  );
};


const ScoreRing: React.FC<{ value: number }> = ({ value }) => {
  const size = 72;
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - value / 100);
  return (
    <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg className="w-full h-full -rotate-90" viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={scoreColor(value)} strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <span className="absolute font-mono text-label-sm font-bold" style={{ color: '#0f172a' }}>{value}%</span>
    </div>
  );
};

const ScoreRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="flex flex-col gap-1.5 p-3 rounded-xl"
    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
    <div className="flex items-center justify-between">
      <span className="font-body text-body-sm" style={{ color: '#475569' }}>{label}</span>
      <span className="font-mono text-label-md font-bold" style={{ color: scoreColor(value) }}>{Math.round(value)}%</span>
    </div>
    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
      <div className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(value, 100)}%`, background: scoreColor(value) }} />
    </div>
  </div>
);

// ─── Article History timeline ─────────────────────────────────────────────────
const ArticleHistory: React.FC<{ article: Article }> = ({ article }) => {
  const fmt = (ts?: string) => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    } catch { return ts; }
  };

  const timeAgo = (ts?: string) => {
    if (!ts) return '';
    try {
      const diff = Date.now() - new Date(ts).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1)  return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24)  return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    } catch { return ''; }
  };

  // Build the event list from the article object
  interface HistoryEntry {
    key: string;
    type: 'generated' | 'revision' | 'published' | 'status';
    icon: string;
    color: string;
    bg: string;
    label: string;
    timestamp?: string;
    headline?: string;
    prevHeadline?: string;
    feedback?: string;
    model?: string;
    scores?: { style?: number; originality?: number; fact?: number; words?: number };
  }

  const entries: HistoryEntry[] = [];

  // 1. Generation event
  entries.push({
    key: 'generated',
    type: 'generated',
    icon: 'auto_awesome',
    color: '#4f46e5',
    bg: 'rgba(79,70,229,0.08)',
    label: 'Article Generated',
    timestamp: article.created_at,
    headline: article.original_headline || article.headline,
    scores: {
      style:       article.style_match_score,
      originality: article.originality_score ?? (100 - (article.plagiarism_score || 0)),
      fact:        article.style_compliance_score || article.confidence_score,
      words:       (article.content || '').split(/\s+/).filter(Boolean).length,
    },
  });

  // 2. Revision events
  (article.revision_history || []).forEach((rev: any, i: number) => {
    entries.push({
      key: `rev-${i}`,
      type: 'revision',
      icon: 'edit',
      color: '#f59e0b',
      bg: 'rgba(245,158,11,0.08)',
      label: `Revision #${i + 1}`,
      timestamp: rev.timestamp,
      headline: rev.revised_headline,
      prevHeadline: rev.previous_headline,
      feedback: rev.feedback,
      model: rev.model_used,
    });
  });

  // 3. Published event (if status is published and we have a timestamp)
  if (article.status === 'published' && article.created_at) {
    const lastRev = (article.revision_history || []).slice(-1)[0];
    entries.push({
      key: 'published',
      type: 'published',
      icon: 'rocket_launch',
      color: '#10b981',
      bg: 'rgba(16,185,129,0.08)',
      label: 'Published',
      timestamp: lastRev?.timestamp || article.created_at,
      headline: article.headline,
    });
  }

  // Sort by timestamp ascending
  entries.sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return ta - tb;
  });

  if (entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 36, display: 'block', marginBottom: 8 }}>history</span>
        <div style={{ fontSize: 13 }}>No history yet</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Vertical line */}
      <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: '#e2e8f0' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {entries.map((entry, idx) => {
          const isLast = idx === entries.length - 1;
          return (
            <div key={entry.key} style={{ display: 'flex', gap: 10, paddingBottom: isLast ? 0 : 10 }}>
              {/* Icon */}
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: entry.bg,
                border: `2px solid ${entry.color}30`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, zIndex: 1,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: entry.color }}>{entry.icon}</span>
              </div>

              {/* Body */}
              <div style={{
                flex: 1, background: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '8px 12px', minWidth: 0,
              }}>
                {/* Label + time */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: entry.color,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    {entry.label}
                  </span>
                  <span style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right', flexShrink: 0, marginLeft: 8 }}
                    title={fmt(entry.timestamp)}>
                    {timeAgo(entry.timestamp)}
                  </span>
                </div>

                {/* Full timestamp */}
                <div style={{ fontSize: 10, color: '#cbd5e1', marginBottom: 6 }}>
                  {fmt(entry.timestamp)}
                </div>

                {/* Headline */}
                {entry.headline && (
                  <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 500, lineHeight: 1.4, marginBottom: entry.prevHeadline || entry.feedback || entry.scores ? 6 : 0 }}>
                    {entry.headline}
                  </div>
                )}

                {/* Previous headline (for revisions) */}
                {entry.prevHeadline && entry.prevHeadline !== entry.headline && (
                  <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4 }}>was:</span>
                    "{entry.prevHeadline}"
                  </div>
                )}

                {/* Feedback */}
                {entry.feedback && (
                  <div style={{
                    fontSize: 11, color: '#92400e', background: 'rgba(245,158,11,0.07)',
                    borderLeft: '3px solid #f59e0b', padding: '4px 8px', borderRadius: '0 4px 4px 0',
                    marginBottom: entry.model ? 4 : 0, lineHeight: 1.5,
                  }}>
                    {entry.feedback}
                  </div>
                )}

                {/* Model used */}
                {entry.model && (
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 11, verticalAlign: 'middle' }}>smart_toy</span>
                    {' '}{entry.model}
                  </div>
                )}

                {/* Generation scores */}
                {entry.scores && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {entry.scores.style != null && (
                      <ScorePill label="Style" value={entry.scores.style} />
                    )}
                    {entry.scores.originality != null && (
                      <ScorePill label="Orig" value={entry.scores.originality} />
                    )}
                    {entry.scores.fact != null && (
                      <ScorePill label="Fact" value={entry.scores.fact} />
                    )}
                    {entry.scores.words != null && (
                      <span style={{ fontSize: 10, color: '#94a3b8', padding: '2px 6px', background: '#f1f5f9', borderRadius: 4 }}>
                        {entry.scores.words} words
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ScorePill: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const c = value >= 85 ? '#34d399' : value >= 70 ? '#4f46e5' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <span style={{
      fontSize: 10, padding: '2px 6px', borderRadius: 4,
      background: `${c}15`, color: c, fontWeight: 600, border: `1px solid ${c}30`,
    }}>
      {label} {Math.round(value)}%
    </span>
  );
};

// ─── Validation stage badge ───────────────────────────────────────────────────
// Maps a stage result status to a coloured badge. Treats "skipped" distinctly
// from "pass" so a stage that was not run never shows as a green OK.
const stageBadgeCfg: Record<string, { color: string; bg: string; border: string; label: string; icon: string }> = {
  pass:         { color: '#34d399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.3)',  label: 'PASS',    icon: 'check_circle' },
  fail:         { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   label: 'FAIL',    icon: 'cancel' },
  needs_review: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)',  label: 'REVIEW',  icon: 'pending' },
  skipped:      { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',border: 'rgba(148,163,184,0.3)', label: 'SKIPPED', icon: 'block' },
};

const ValidationStageBadge: React.FC<{ label: string; result?: ValidationStageResult }> = ({ label, result }) => {
  if (!result) return null;
  const status = result.status || 'skipped';
  const cfg = stageBadgeCfg[status] || stageBadgeCfg['skipped'];
  const tooltip = status === 'skipped'
    ? (result.skip_reason === 'upstream_factual_gate_failed'
      ? 'Not run — an earlier gate failed'
      : result.skip_reason || 'Stage skipped')
    : `${result.passed} passed, ${result.failed} failed`;
  return (
    <div
      title={tooltip}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 10px', borderRadius: 8,
        background: cfg.bg, border: `1px solid ${cfg.border}`,
      }}
    >
      <span style={{ fontSize: 11, color: '#475569', fontFamily: 'Inter, sans-serif' }}>{label}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 10, fontWeight: 700, color: cfg.color,
        letterSpacing: '0.04em',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{cfg.icon}</span>
        {cfg.label}
      </span>
    </div>
  );
};

export default ArticleInspector;
